import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  computePaginationWithCount,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
  pageToCursor,
} from '@/lib/graphql-pagination';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { CollectionCase } from '@/types/collections';
import { useModeAwareQuery } from './useModeAwareQuery';

export type { PaginationResult } from '@/lib/graphql-pagination';

// --- Params ---

export interface GetCollectionCasesParams extends Record<string, string | number | boolean | string[] | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  statuses?: string[];
  priority?: string;
  assignedToUserId?: number;
  currencies?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  nextActionDateFrom?: string;
  nextActionDateTo?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  outstandingMin?: number;
  outstandingMax?: number;
  daysInCollectionMin?: number;
  daysInCollectionMax?: number;
  grossMin?: number;
  grossMax?: number;
  stageMin?: number;
  stageMax?: number;
  strategies?: string[];
  lastActionTypes?: string[];
}

// --- GraphQL types ---

interface CollectionCaseGraphQLNode {
  caseId: number;
  caseNumber: string | null;
  customerId: number;
  customerName: string | null;
  customerAccountNumber: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  priority: string | null;
  status: string | null;
  strategyId: number | null;
  strategyName: string | null;
  amount: number;
  baseAmount?: number;
  displayOutstandingAmount?: number;
  grossAmount?: number;
  displayGrossAmount?: number;
  daysInCollection: number;
  nextAction: string | null;
  nextActionDate: string | null;
  nextActionDateSort: string;
  prioritySortOrder: number;
  assignedToUserId: number | null;
  assignedToName: string | null;
  contactId: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  activityCount: number;
  transactionCount: number;
  lastActionType: string | null;
  lastActionDescription: string | null;
  lastActionActorName: string | null;
  lastActionDate: string | null;
  currentStage: number;
  totalStages: number;
  currencyCode: string | null;
}

interface CollectionCaseListGraphQLResponse {
  collectionCases: {
    collectionCaseList: GraphQLConnection<CollectionCaseGraphQLNode>;
  };
}

interface CollectionCaseCountGraphQLResponse {
  collectionCases: {
    collectionCaseCount: number;
  };
}

interface CollectionCaseTotalsGraphQLResponse {
  collectionCases: {
    collectionCaseTotals: {
      totalCount: number;
      promisedCount: number;
      highPriorityCount: number;
      dueTodayCount: number;
      totalOutstanding: number;
      totalBaseOutstanding: number;
      totalDisplayOutstanding: number;
      totalGross: number;
      totalBaseGross: number;
      totalDisplayGross: number;
    };
  };
}

// --- GraphQL queries ---

const COLLECTION_CASE_LIST_QUERY = `
  query CollectionCaseList($first: Int, $after: String, $sort: [CollectionCaseListSortSortByInput!], $filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            caseId
            caseNumber
            customerId
            customerName
            customerAccountNumber
            customerEmail
            customerPhone
            priority
            status
            strategyId
            strategyName
            amount
            baseAmount
            displayOutstandingAmount
            grossAmount
            displayGrossAmount
            daysInCollection
            nextAction
            nextActionDate
            nextActionDateSort
            prioritySortOrder
            assignedToUserId
            assignedToName
            contactId
            notes
            createdAt
            updatedAt
            activityCount
            transactionCount
            lastActionType
            lastActionDescription
            lastActionActorName
            lastActionDate
            currentStage
            totalStages
            currencyCode
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const COLLECTION_CASE_COUNT_QUERY = `
  query CollectionCaseCount($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseCount(filter: $filter)
    }
  }
`;

const COLLECTION_CASE_TOTALS_QUERY = `
  query CollectionCaseTotals($filter: CollectionCaseListFilterInput) {
    collectionCases {
      collectionCaseTotals(filter: $filter) {
        totalCount
        promisedCount
        highPriorityCount
        dueTodayCount
        totalOutstanding
        totalBaseOutstanding
        totalDisplayOutstanding
        totalGross
        totalBaseGross
        totalDisplayGross
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToCollectionCase(node: CollectionCaseGraphQLNode): CollectionCase {
  const lastAction = node.lastActionType
    ? {
        type: node.lastActionType,
        description: node.lastActionDescription || '',
        date: node.lastActionDate ? new Date(node.lastActionDate) : new Date(),
        performedBy: node.lastActionActorName || 'Unknown',
      }
    : undefined;

  return {
    id: String(node.caseId),
    caseId: String(node.caseId),
    caseNumber: node.caseNumber || undefined,
    customerId: String(node.customerId),
    customerAccountNumber: node.customerAccountNumber || undefined,
    customerName: node.customerName || '',
    customerEmail: node.customerEmail || '',
    customerPhone: node.customerPhone || '',
    priority: (node.priority as CollectionCase['priority']) || 'Medium',
    status: (node.status as CollectionCase['status']) || 'Active',
    strategy: {
      id: node.strategyId ? String(node.strategyId) : '',
      name: node.strategyId ? node.strategyName || 'Default' : 'Manual',
      type: 'Standard',
    },
    strategyPhase: node.strategyId
      ? {
          currentPhase: node.currentStage,
          totalPhases: node.totalStages,
          phaseName: `Stage ${node.currentStage}`,
        }
      : {
          currentPhase: 1,
          totalPhases: 1,
          phaseName: 'Manual',
        },
    amount: node.amount,
    baseAmount: node.baseAmount ?? 0,
    displayOutstandingAmount: node.displayOutstandingAmount ?? 0,
    grossAmount: node.grossAmount ?? 0,
    displayGrossAmount: node.displayGrossAmount ?? 0,
    daysInCollection: node.daysInCollection,
    nextAction: node.nextAction || 'No further actions scheduled',
    nextActionDate: node.nextActionDate ? new Date(node.nextActionDate) : new Date('9999-12-31'),
    createdAt: new Date(node.createdAt),
    updatedAt: new Date(node.updatedAt),
    assignedTo: node.assignedToName || undefined,
    assignedUserId: node.assignedToUserId ?? undefined,
    contactId: node.contactId ? String(node.contactId) : undefined,
    notes: node.notes || undefined,
    currency: node.currencyCode || undefined,
    transactionCount: node.transactionCount ?? 0,
    lastAction,
  };
}

// --- Sort / Filter builders ---

export const sortFieldMap: Record<string, string> = {
  priority: 'PrioritySortOrder',
  caseId: 'CaseNumber',
  customerName: 'CustomerName',
  customerId: 'CustomerId',
  customerAccountNumber: 'CustomerAccountNumber',
  customerEmail: 'CustomerEmail',
  customerPhone: 'CustomerPhone',
  currency: 'CurrencyCode',
  gross: 'GrossAmount',
  outstanding: 'BaseAmount',
  daysInCollection: 'DaysInCollection',
  transactionCount: 'TransactionCount',
  nextAction: 'NextAction',
  nextActionDate: 'NextActionDateSort',
  status: 'Status',
  strategy: 'StrategyName',
  stage: 'CurrentStage',
  assignedTo: 'AssignedToName',
  createdAt: 'CreatedAt',
  updatedAt: 'UpdatedAt',
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: 'asc' | 'desc') {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildRangeFilter<T>(min: T | undefined, max: T | undefined): Record<string, T> | undefined {
  if (min === undefined && max === undefined) return undefined;
  const range: Record<string, T> = {};
  if (min !== undefined) range.ge = min;
  if (max !== undefined) range.le = max;
  return range;
}

function buildContainsFilter(values: string[] | undefined): { containsValues: string[] } | undefined {
  return values && values.length > 0 ? { containsValues: values } : undefined;
}

// createdAt/updatedAt are DateTime fields, but the date pickers emit date-only strings (yyyy-MM-dd).
// Convert the range to a full DateTime span (start-of-day..end-of-day) via the DateTime filter's
// dateOnly sub-filter, mirroring buildDateTimeDateOnlyFilter in usePromisesToPayData.ts.
function buildDateTimeDateOnlyFilter(from?: string, to?: string): { dateOnly: Record<string, string> } | undefined {
  if (!from && !to) return undefined;
  const range: Record<string, string> = {};
  if (from) range.ge = `${from}T00:00:00`;
  if (to) range.le = `${to}T23:59:59`;
  return { dateOnly: range };
}

export function buildGraphQLFilterVariable(params: GetCollectionCasesParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.statuses && params.statuses.length > 0) {
    filter.status = params.statuses.length === 1 ? { eq: params.statuses[0] } : { containsValues: params.statuses };
  } else if (params.status && params.status !== 'all') {
    filter.status = { eq: params.status };
  }
  if (params.priority && params.priority !== 'all') {
    filter.priority = { eq: params.priority };
  }
  if (params.assignedToUserId !== undefined) {
    filter.assignedToUserId = { eq: params.assignedToUserId };
  }
  if (params.currencies && params.currencies.length > 0) {
    if (params.currencies.length === 1) {
      filter.currencyCode = { eq: params.currencies[0] };
    } else {
      filter.currencyCode = { containsValues: params.currencies };
    }
  }

  const nextActionDateRange = buildRangeFilter(params.nextActionDateFrom, params.nextActionDateTo);
  if (nextActionDateRange) filter.nextActionDate = nextActionDateRange;

  const createdRange = buildDateTimeDateOnlyFilter(params.createdFrom, params.createdTo);
  if (createdRange) filter.createdAt = createdRange;

  const updatedRange = buildDateTimeDateOnlyFilter(params.updatedFrom, params.updatedTo);
  if (updatedRange) filter.updatedAt = updatedRange;

  const outstandingRange = buildRangeFilter(params.outstandingMin, params.outstandingMax);
  if (outstandingRange) filter.baseAmount = outstandingRange;

  const daysRange = buildRangeFilter(params.daysInCollectionMin, params.daysInCollectionMax);
  if (daysRange) filter.daysInCollection = daysRange;

  const grossRange = buildRangeFilter(params.grossMin, params.grossMax);
  if (grossRange) filter.grossAmount = grossRange;

  const stageRange = buildRangeFilter(params.stageMin, params.stageMax);
  if (stageRange) filter.currentStage = stageRange;

  const strategiesFilter = buildContainsFilter(params.strategies);
  if (strategiesFilter) filter.strategyName = strategiesFilter;

  const actionTypeFilter = buildContainsFilter(params.lastActionTypes);
  if (actionTypeFilter) filter.lastActionType = actionTypeFilter;

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mock pagination helper ---

function computeMockCasePagination(cases: CollectionCase[], page: number, pageSize: number): PaginationResult {
  return computeMockPagination(cases.length, page, pageSize);
}

function filterMockCases(cases: CollectionCase[], params: GetCollectionCasesParams): CollectionCase[] {
  return cases.filter((caseItem) => {
    const matchesSearch =
      !params.search ||
      caseItem.customerName.toLowerCase().includes(params.search.toLowerCase()) ||
      caseItem.caseId.toLowerCase().includes(params.search.toLowerCase());

    const matchesStatus =
      params.statuses && params.statuses.length > 0
        ? params.statuses.includes(caseItem.status)
        : !params.status || params.status === 'all' || caseItem.status === params.status;
    const matchesPriority = !params.priority || params.priority === 'all' || caseItem.priority === params.priority;
    const matchesUser = params.assignedToUserId === undefined || caseItem.assignedTo !== undefined;
    const matchesCurrency =
      !params.currencies ||
      params.currencies.length === 0 ||
      (caseItem.currency !== undefined && params.currencies.includes(caseItem.currency));

    const matchesNextActionDate = (() => {
      if (!params.nextActionDateFrom && !params.nextActionDateTo) return true;
      const date = new Date(caseItem.nextActionDate);
      if (date.getFullYear() === 9999) return false;
      const dateStr = date.toISOString().split('T')[0];
      if (params.nextActionDateFrom && dateStr < params.nextActionDateFrom) return false;
      if (params.nextActionDateTo && dateStr > params.nextActionDateTo) return false;
      return true;
    })();

    const matchesCreated = (() => {
      if (!params.createdFrom && !params.createdTo) return true;
      const dateStr = caseItem.createdAt.toISOString().split('T')[0];
      if (params.createdFrom && dateStr < params.createdFrom) return false;
      if (params.createdTo && dateStr > params.createdTo) return false;
      return true;
    })();

    const matchesUpdated = (() => {
      if (!params.updatedFrom && !params.updatedTo) return true;
      const dateStr = caseItem.updatedAt.toISOString().split('T')[0];
      if (params.updatedFrom && dateStr < params.updatedFrom) return false;
      if (params.updatedTo && dateStr > params.updatedTo) return false;
      return true;
    })();

    const matchesOutstanding = (() => {
      const val = caseItem.baseAmount ?? 0;
      if (params.outstandingMin !== undefined && val < params.outstandingMin) return false;
      if (params.outstandingMax !== undefined && val > params.outstandingMax) return false;
      return true;
    })();

    const matchesDays = (() => {
      if (params.daysInCollectionMin !== undefined && caseItem.daysInCollection < params.daysInCollectionMin)
        return false;
      if (params.daysInCollectionMax !== undefined && caseItem.daysInCollection > params.daysInCollectionMax)
        return false;
      return true;
    })();

    const matchesGross = (() => {
      const val = caseItem.grossAmount ?? 0;
      if (params.grossMin !== undefined && val < params.grossMin) return false;
      if (params.grossMax !== undefined && val > params.grossMax) return false;
      return true;
    })();

    const matchesStage = (() => {
      const val = caseItem.strategyPhase?.currentPhase ?? 0;
      if (params.stageMin !== undefined && val < params.stageMin) return false;
      if (params.stageMax !== undefined && val > params.stageMax) return false;
      return true;
    })();

    const matchesStrategy =
      !params.strategies || params.strategies.length === 0 || params.strategies.includes(caseItem.strategy.name);

    const matchesActionType =
      !params.lastActionTypes ||
      params.lastActionTypes.length === 0 ||
      (caseItem.lastAction?.type !== undefined && params.lastActionTypes.includes(caseItem.lastAction.type));

    return (
      matchesSearch &&
      matchesStatus &&
      matchesPriority &&
      matchesUser &&
      matchesCurrency &&
      matchesNextActionDate &&
      matchesCreated &&
      matchesUpdated &&
      matchesOutstanding &&
      matchesDays &&
      matchesGross &&
      matchesStage &&
      matchesStrategy &&
      matchesActionType
    );
  });
}

// --- Result types ---

export interface CollectionCasesListResult {
  cases: CollectionCase[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useCollectionCaseCount(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ['collection-cases-count', filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CollectionCaseCountGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_COUNT_QUERY,
        variables,
        signal,
      );

      return response.collectionCases.collectionCaseCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}

export function useCollectionCasesListGraphQL(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const mockCases = getGlobalDataStore().getAllCases();
  const queryParams: GetCollectionCasesParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'nextActionDate',
    sortOrder: 'asc',
    ...params,
  };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const filteredMock = filterMockCases(mockCases, queryParams);
  const mockPagination = computeMockCasePagination(filteredMock, mockPage, mockPageSize);
  const mockPagedCases = filteredMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: CollectionCasesListResult = { cases: mockPagedCases, pagination: mockPagination };

  return useModeAwareQuery<CollectionCasesListResult, Error>(
    ['collection-cases-list', queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 25;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<CollectionCaseListGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.collectionCases.collectionCaseList;
      const cases = connection.edges.map((x) => mapGraphQLNodeToCollectionCase(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: cases.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { cases, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

// --- Totals result type ---

export interface CollectionCaseTotalsResult {
  totalCount: number;
  promisedCount: number;
  highPriorityCount: number;
  dueTodayCount: number;
  totalOutstanding: number;
  totalBaseOutstanding: number;
  totalDisplayOutstanding: number;
  totalGross: number;
  totalBaseGross: number;
  totalDisplayGross: number;
}

// --- Totals hook ---

export function useCollectionCaseTotals(params?: GetCollectionCasesParams) {
  const apiConfig = useApiConfig();
  const filter = buildGraphQLFilterVariable(params ?? {});

  const mockCases = getGlobalDataStore().getAllCases();
  const filteredMock = filterMockCases(mockCases, params ?? {});
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mockTotals: CollectionCaseTotalsResult = {
    totalCount: filteredMock.length,
    promisedCount: filteredMock.filter((x) => x.status === 'Promised').length,
    highPriorityCount: filteredMock.filter((x) => x.priority === 'High').length,
    dueTodayCount: filteredMock.filter((x) => {
      if (x.nextAction === 'No further actions scheduled') return false;
      const actionDate = new Date(x.nextActionDate);
      if (actionDate.getFullYear() === 9999) return false;
      actionDate.setHours(0, 0, 0, 0);
      return actionDate.getTime() === today.getTime();
    }).length,
    totalOutstanding: filteredMock.reduce((sum, x) => sum + (x.baseAmount ?? 0), 0),
    totalBaseOutstanding: filteredMock.reduce((sum, x) => sum + (x.baseAmount ?? 0), 0),
    totalDisplayOutstanding: filteredMock.reduce((sum, x) => sum + (x.displayOutstandingAmount ?? 0), 0),
    totalGross: filteredMock.reduce((sum, x) => sum + (x.grossAmount ?? 0), 0),
    totalBaseGross: filteredMock.reduce((sum, x) => sum + (x.grossAmount ?? 0), 0),
    totalDisplayGross: filteredMock.reduce((sum, x) => sum + (x.displayGrossAmount ?? 0), 0),
  };

  return useModeAwareQuery<CollectionCaseTotalsResult, Error>(
    ['collection-cases-totals', filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<CollectionCaseTotalsGraphQLResponse>(
        apiConfig,
        COLLECTION_CASE_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.collectionCases.collectionCaseTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: 'always',
    },
  );
}

// --- Composite page data hook ---

const defaultPagination = createDefaultPagination(25);

export function useCollectionCasesPageData(params?: GetCollectionCasesParams) {
  const listQuery = useCollectionCasesListGraphQL(params);
  const countQuery = useCollectionCaseCount(params);

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    cases: listQuery.data?.cases ?? [],
    pagination,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
    },
  };
}
